import { computed, ref } from 'vue'
import { authMessages } from './modules/auth'
import { branchMessages } from './modules/branch'
import { customerMessages } from './modules/customer'
import { channelMessages } from './modules/channel'
import { inventoryMessages } from './modules/inventory'
import { manufacturingMessages } from './modules/manufacturing'
import { priceMessages } from './modules/price'
import { publicMessages } from './modules/public'
import { productMessages } from './modules/product'
import { salesMessages } from './modules/sales'
import { saleReturnMessages } from './modules/sale-return'
import { staffMessages } from './modules/staff'
import { sharedMessages } from './modules/shared'
import { supplierMessages } from './modules/supplier'
import { buttons } from './modules/shared/button'

import type { Locale, LocaleMessageBundle, LocaleMessages, TranslationParams } from './types'

export type { Locale, TranslationParams } from './types'

const currentLocale = ref<Locale>('vi')

const messageBundles: LocaleMessageBundle[] = [
  sharedMessages,
  authMessages,
  productMessages,
  priceMessages,
  publicMessages,
  salesMessages,
  saleReturnMessages,
  branchMessages,
  customerMessages,
  channelMessages,
  staffMessages,
  inventoryMessages,
  manufacturingMessages,
  supplierMessages,
  buttons,
]

const legacyAliases: Record<string, string> = {
  'login.title': 'auth.login.title',
  'login.subtitle': 'auth.login.subtitle',
  'login.tenantCode': 'auth.login.fields.tenantCode',
  'login.tenantHint': 'auth.login.placeholders.tenantCode',
  'login.username': 'auth.login.fields.username',
  'login.usernameHint': 'auth.login.placeholders.username',
  'login.password': 'auth.login.fields.password',
  'login.passwordHint': 'auth.login.placeholders.password',
  'login.remember': 'auth.login.fields.remember',
  'login.forgotPassword': 'auth.login.actions.forgotPassword',
  'login.submit': 'auth.login.actions.submit',
  'login.submitting': 'auth.login.actions.submitting',
  'login.version': 'auth.login.meta.version',
  'login.language': 'auth.login.language',
  'auth.mockError': 'auth.login.errors.mockError',
}

const messages: Record<Locale, LocaleMessages> = {
  vi: mergeLocaleMessages('vi'),
  en: mergeLocaleMessages('en'),
}

function mergeLocaleMessages(locale: Locale) {
  return messageBundles.reduce<LocaleMessages>(
    (mergedMessages, bundle) => ({
      ...mergedMessages,
      ...bundle[locale],
    }),
    {},
  )
}

function resolveKey(key: string) {
  return legacyAliases[key] ?? key
}

function interpolate(message: string, params?: TranslationParams) {
  if (!params) {
    return message
  }

  return message.replace(/\{([a-zA-Z0-9_]+)\}/g, (placeholder, paramKey: string) => {
    const value = params[paramKey]

    return value === null || value === undefined ? placeholder : String(value)
  })
}

export function setLocale(locale: Locale) {
  currentLocale.value = locale
}

export function getCurrentLocale() {
  return currentLocale.value
}

export function translate(key: string, params?: TranslationParams) {
  const resolvedKey = resolveKey(key)
  const message = messages[currentLocale.value][resolvedKey] ?? messages.vi[resolvedKey] ?? key

  return interpolate(message, params)
}

export function useI18n() {
  return {
    locale: currentLocale,
    localeOptions: computed(() => [
      { title: 'Tiếng Việt', value: 'vi' },
      { title: 'English', value: 'en' },
    ]),
    setLocale,
    t: translate,
  }
}
